import { useApolloClient, useMutation } from '@apollo/client';
import {
    AddOrUpdateWorldMapMarker,
    AddOrUpdateWorldMapMarkerVariables
} from 'graphql/mutation/__generated__/AddOrUpdateWorldMapMarker';
import {
    RemoveWorldMapMarker,
    RemoveWorldMapMarkerVariables
} from 'graphql/mutation/__generated__/RemoveWorldMapMarker';
import {
    ReorderWorldMarkers,
    ReorderWorldMarkersVariables
} from 'graphql/mutation/__generated__/ReorderWorldMarkers';
import {
    UpdateWorldMap,
    UpdateWorldMapVariables
} from 'graphql/mutation/__generated__/UpdateWorldMap';
import {
    ADD_OR_UPDATE_WORLD_MAP_MARKER,
    REMOVE_WORLD_MAP_MARKER_MUTATION,
    REORDER_MARKERS_MUTATION,
    UPDATE_WORLD_MAP_MUTATION
} from 'graphql/mutation/worldMutation';
import { cacheAddMarker } from 'hooks/marker/cacheUpdateMethods';
import { useWorldMapLocalStorage } from 'libs/hooks/ownership/useWorldMapLocalStorage';
import {
    MarkerHistoryRecord,
    ReorderMarkersHistoryRecord,
    UpdateMapRecord,
    isMarkerHistoryRecord,
    isReorderMarkersHistoryRecord
} from 'libs/types/history/historyTypes';
import { EToastType } from 'libs/types/site/IClient';
import { userMessage } from 'libs/utils/logic/ui/uiLogic';
import { markerToGraphQL } from 'libs/utils/mapping/worldGrahQLMaps';
import { omitDeep } from 'libs/utils/object/objectOperations';
import { uniq } from 'lodash';
import { useContext, useEffect } from 'react';
import { CtxEdit } from 'session/ctxEdit';

export const ServerSync = () => {
    const { getTokenForMapId } = useWorldMapLocalStorage();

    const ctxEdit = useContext(CtxEdit);

    const apolloClient = useApolloClient();

    const [addOrUpdateMarker] = useMutation<
        AddOrUpdateWorldMapMarker,
        AddOrUpdateWorldMapMarkerVariables
    >(ADD_OR_UPDATE_WORLD_MAP_MARKER);

    const [removeMarker] = useMutation<RemoveWorldMapMarker, RemoveWorldMapMarkerVariables>(
        REMOVE_WORLD_MAP_MARKER_MUTATION
    );

    const [reorderMarkers] = useMutation<ReorderWorldMarkers, ReorderWorldMarkersVariables>(
        REORDER_MARKERS_MUTATION
    );

    const [updateMap] = useMutation<UpdateWorldMap, UpdateWorldMapVariables>(
        UPDATE_WORLD_MAP_MUTATION
    );

    useEffect(() => {
        const interval = setInterval(() => {
            const allMarkersOperations =
                ctxEdit.editHistory.state.serverQueue.filter<MarkerHistoryRecord>(
                    isMarkerHistoryRecord
                );

            const markerIds = uniq(allMarkersOperations.map((e) => e.data.id));

            for (const markerId of markerIds) {
                const operations = allMarkersOperations.filter((e) => e.data.id === markerId);

                const operation = operations[operations.length - 1];
                if (operation.operation === 'delete') {
                    removeMarker({
                        variables: {
                            worldMapId: ctxEdit.map?.worldMapId,
                            worldMapMarkerId: operation.data.id,
                            token: getTokenForMapId(ctxEdit.map?.worldMapId)
                        }
                    });
                }
                if (operation.operation === 'add' || operation.operation === 'update') {
                    addOrUpdateMarker({
                        variables: {
                            worldMapMarkerId: operation.data.id,
                            worldMapId: ctxEdit.map?.worldMapId,
                            marker: markerToGraphQL(operation.data),
                            token: getTokenForMapId(ctxEdit.map?.worldMapId)
                        },
                        optimisticResponse: {
                            addOrUpdateWorldMapMarker: {
                                worldMapMarkerId: 'new',
                                worldMapSets: [],
                                gptContextId: null,
                                layerTitle: null,
                                story: null,
                                stories: [],
                                __typename: 'WorldMapMarker'
                            }
                        },
                        update: (cache, result) => {
                            cacheAddMarker(
                                cache,
                                ctxEdit.map?.worldMapId,
                                result.data.addOrUpdateWorldMapMarker
                            );
                            // This is to update cache for floors when the marker is added/duplicated
                            apolloClient.refetchQueries({ include: ['WorldMapMarkerStory'] });
                        },
                        onError: () => {
                            userMessage(EToastType.ERROR, 'Cannot add or update marker');
                        }
                    });
                }
            }

            if (allMarkersOperations.length) {
                ctxEdit.editHistory.actions.removeFromServerQueue(
                    allMarkersOperations.map((e) => e.recordId)
                );
            }

            const allReorderOperations =
                ctxEdit.editHistory.state.serverQueue.filter<ReorderMarkersHistoryRecord>(
                    isReorderMarkersHistoryRecord
                );

            if (allReorderOperations.length) {
                const operation = allReorderOperations[allReorderOperations.length - 1];

                reorderMarkers({
                    variables: {
                        worldMapId: ctxEdit.map?.worldMapId,
                        order: operation.data,
                        token: getTokenForMapId(ctxEdit.map.worldMapId)
                    },
                    optimisticResponse: {
                        reorderWorldMarkers: operation.data.map((m) => ({
                            worldMapMarkerId: m.worldMapMarkerId,
                            order: m.order,
                            __typename: 'WorldMapMarker'
                        }))
                    }
                });

                ctxEdit.editHistory.actions.removeFromServerQueue(
                    allReorderOperations.map((e) => e.recordId)
                );
            }

            const allMapUpdateOperations =
                ctxEdit.editHistory.state.serverQueue.filter<UpdateMapRecord>(
                    (e): e is UpdateMapRecord => e.operation === 'updateMap'
                );

            for (const operation of allMapUpdateOperations) {
                updateMap({
                    variables: {
                        worldMapId: ctxEdit.map?.worldMapId,
                        token: getTokenForMapId(ctxEdit.map?.worldMapId),
                        map: {
                            ...omitDeep(operation.data, ['__typename'])
                        }
                    }
                });

                ctxEdit.editHistory.actions.removeFromServerQueue(
                    allMapUpdateOperations.map((e) => e.recordId)
                );
            }
        }, 100);

        return () => {
            clearInterval(interval);
        };
    });

    return <></>;
};
